import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { Capacitor } from "@capacitor/core";
import splashVideoUrl from "@/assets/splash-animation.mp4?url";
import logoFallback from "@/assets/logo.webp";

const FADE_OUT_MS = 250;
const FAILSAFE_MS = 8000;
/** If the video element errors, show logo then dismiss */
const ERROR_FALLBACK_MS = 3000;

const SPLASH_DEBUG =
  import.meta.env.DEV || import.meta.env.VITE_DEBUG_SPLASH === "true";

function logSplash(message: string, data?: Record<string, unknown>) {
  if (!SPLASH_DEBUG) return;
  if (data) {
    console.info(`[AnimatedSplash] ${message}`, data);
  } else {
    console.info(`[AnimatedSplash] ${message}`);
  }
}

interface AnimatedSplashProps {
  isReady?: boolean;
}

/**
 * Local MP4 via Vite `?url` (hashed in dist) so WebView always resolves a real file under /assets/.
 * No dismiss on `error` before fallback — we show a static logo, then dismiss.
 */
export function AnimatedSplash({ isReady: _isReady = false }: AnimatedSplashProps) {
  const [fading, setFading] = useState(false);
  const [mounted, setMounted] = useState(true);
  const [videoVisible, setVideoVisible] = useState(false);
  const [videoError, setVideoError] = useState(false);
  const failSafeRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const errorDismissRef = useRef<ReturnType<typeof setTimeout> | null>(null);
  const videoRef = useRef<HTMLVideoElement>(null);
  const dismissedRef = useRef(false);
  const frameShownRef = useRef(false);
  const playStartedRef = useRef(false);
  const loadedDataOnceRef = useRef(false);

  const videoSrc = useMemo(() => splashVideoUrl, []);

  const clearFailSafe = useCallback(() => {
    if (failSafeRef.current) {
      clearTimeout(failSafeRef.current);
      failSafeRef.current = null;
    }
  }, []);

  const clearErrorDismiss = useCallback(() => {
    if (errorDismissRef.current) {
      clearTimeout(errorDismissRef.current);
      errorDismissRef.current = null;
    }
  }, []);

  const markFrameVisible = useCallback(() => {
    if (frameShownRef.current) return;
    frameShownRef.current = true;
    setVideoVisible(true);
  }, []);

  const dismiss = useCallback(() => {
    if (dismissedRef.current) return;
    dismissedRef.current = true;
    clearFailSafe();
    clearErrorDismiss();
    setFading(true);
    setTimeout(() => {
      setMounted(false);
    }, FADE_OUT_MS);
  }, [clearFailSafe, clearErrorDismiss]);

  useEffect(() => {
    if (!Capacitor.isNativePlatform()) {
      setMounted(false);
    }
  }, []);

  useEffect(() => {
    if (!SPLASH_DEBUG || !Capacitor.isNativePlatform() || !mounted) return;
    logSplash("mount", {
      platform: Capacitor.getPlatform(),
      origin: typeof window !== "undefined" ? window.location.origin : "",
      videoSrc,
    });
  }, [mounted, videoSrc]);

  useEffect(() => {
    if (!Capacitor.isNativePlatform() || !mounted) return;

    failSafeRef.current = setTimeout(() => {
      logSplash(`Failsafe: hiding after ${FAILSAFE_MS}ms`);
      dismiss();
    }, FAILSAFE_MS);

    return () => {
      clearFailSafe();
    };
  }, [mounted, clearFailSafe, dismiss]);

  useLayoutEffect(() => {
    if (!Capacitor.isNativePlatform() || !mounted || videoError) return;
    const v = videoRef.current;
    if (!v) return;

    v.muted = true;
    v.defaultMuted = true;
    v.setAttribute("muted", "");
    v.playsInline = true;
    v.setAttribute("playsinline", "true");
    v.setAttribute("webkit-playsinline", "true");

    if (playStartedRef.current) return;
    const p = v.play();
    if (p !== undefined) {
      void p
        .then(() => {
          if (!frameShownRef.current) markFrameVisible();
        })
        .catch(() => {
          logSplash("play() in layout (will retry from loadeddata)", { src: videoRef.current?.currentSrc });
        });
    }
  }, [mounted, markFrameVisible, videoError, videoSrc]);

  if (!mounted || !Capacitor.isNativePlatform()) {
    return null;
  }

  const onVideoLoadedData = () => {
    if (loadedDataOnceRef.current) return;
    loadedDataOnceRef.current = true;
    const v = videoRef.current;
    if (!v) return;
    playStartedRef.current = true;
    try {
      v.currentTime = 0;
    } catch {
      /* ignore */
    }
    // Double rAF: some Android WebViews paint the first frame after the next frame(s)
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        markFrameVisible();
        const p = v.play();
        if (p !== undefined) {
          void p.catch(() => {
            logSplash("play() after loadeddata failed", { currentSrc: v.currentSrc });
          });
        }
      });
    });
  };

  const handleVideoError = () => {
    const v = videoRef.current;
    const err = v?.error;
    logSplash("video error", {
      code: err?.code,
      message: err?.message,
      currentSrc: v?.currentSrc,
      networkState: v?.networkState,
      src: videoSrc,
    });
    if (dismissedRef.current) return;
    setVideoError(true);
    markFrameVisible();
    clearErrorDismiss();
    errorDismissRef.current = setTimeout(() => {
      logSplash("dismiss after error fallback");
      dismiss();
    }, ERROR_FALLBACK_MS);
  };

  if (videoError) {
    return (
      <div
        className="fixed inset-0 z-[99999] flex items-center justify-center"
        style={{
          backgroundColor: "#FFFFFF",
          pointerEvents: "auto",
          transition: `opacity ${FADE_OUT_MS}ms ease-out`,
          opacity: fading ? 0 : 1,
        }}
      >
        <img
          src={logoFallback}
          alt=""
          className="h-32 w-32 object-contain select-none"
          style={{ opacity: videoVisible ? 1 : 0, transition: "opacity 150ms linear" }}
          draggable={false}
        />
      </div>
    );
  }

  return (
    <div
      className="fixed inset-0 z-[99999] flex items-center justify-center"
      style={{
        backgroundColor: "#FFFFFF",
        pointerEvents: "auto",
        transition: `opacity ${FADE_OUT_MS}ms ease-out`,
        opacity: fading ? 0 : 1,
      }}
    >
      <video
        key={videoSrc}
        ref={videoRef}
        className="splash-animation-video"
        src={videoSrc}
        autoPlay
        muted
        playsInline
        controls={false}
        disablePictureInPicture
        disableRemotePlayback
        controlsList="nodownload noplaybackrate noremoteplayback"
        tabIndex={-1}
        preload="auto"
        loop={false}
        onContextMenu={(e) => e.preventDefault()}
        onLoadedMetadata={() => {
          const v = videoRef.current;
          if (v && SPLASH_DEBUG && Number.isFinite(v.duration) && v.duration > 0) {
            logSplash("metadata", { duration: v.duration.toFixed(2), currentSrc: v.currentSrc });
          }
        }}
        onLoadedData={onVideoLoadedData}
        onPlaying={markFrameVisible}
        onTimeUpdate={(e) => {
          if (e.currentTarget.currentTime > 0) markFrameVisible();
        }}
        onCanPlay={markFrameVisible}
        onEnded={() => {
          dismiss();
        }}
        onError={handleVideoError}
        style={{
          width: "90vw",
          maxWidth: "90vw",
          height: "auto",
          objectFit: "contain",
          opacity: videoVisible ? 1 : 0,
          transition: "opacity 150ms linear",
        }}
      />
    </div>
  );
}
